import { useCallback, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'hooks/store';
import { ThunkStopCallScribeRecording } from 'store/callScribe/thunks';
import type { RootState } from 'store';
import {
  resolveRecordingState,
  type IRecordingSignal,
  type TRecordingControlState,
} from 'components/CallScribe/recordingStop';

export interface IUseRecordingStopResult {
  recordingState: TRecordingControlState;
  onStopRecording: () => void;
}

export const useRecordingStop = (
  callId: number | undefined,
  call?: IRecordingSignal | null,
): IUseRecordingStopResult => {
  const dispatch = useAppDispatch();
  const [stopping, setStopping] = useState(false);
  const [failed, setFailed] = useState(false);

  const liveIsRecording = useAppSelector((s: RootState) =>
    (typeof callId === 'number' ? s.callScribe.isRecordingByCallId[callId] : undefined));
  const liveStoppedAt = useAppSelector((s: RootState) =>
    (typeof callId === 'number' ? s.callScribe.recordingStoppedAtByCallId[callId] : undefined));

  const recordingState = resolveRecordingState(
    call,
    { stopping, failed },
    { isRecording: liveIsRecording ?? null, recordingStoppedAtUtc: liveStoppedAt ?? null },
  );

  const onStopRecording = useCallback(() => {
    if (typeof callId !== 'number') {
      return;
    }
    setFailed(false);
    setStopping(true);
    void dispatch(ThunkStopCallScribeRecording(callId))
      .unwrap()
      .then(() => {
        setStopping(false);
      })
      .catch(() => {
        setStopping(false);
        setFailed(true);
      });
  }, [callId, dispatch]);

  return { recordingState, onStopRecording };
};

export default useRecordingStop;
